import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useParams, Navigate, Link } from "react-router-dom";
import { 
    ChevronRight, Trophy, Calendar, RefreshCw, Newspaper, ExternalLink, Rss,
    Shield, Zap, TrendingUp, Info 
} from "lucide-react";
import {
    getCompetitionBySlug,
} from "../lib/data";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { ApiFootballWidget } from "../components/ApiFootballWidget";
import leaguesData from "../data/leagues_data.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAP_SLUG_TO_LEAGUE_NAME = {
    "premier-league": "English Premier League",
    "la-liga": "Spanish La Liga",
    "serie-a": "Italian Serie A",
    "bundesliga": "German Bundesliga",
    "ligue-1": "French Ligue 1",
    "champions-league": "UEFA Champions League"
};

// Configuration des widgets API-Football pour chaque compétition supportée
const COMPETITION_WIDGET_CONFIG = {
    "coupe-du-monde": { league: 1, season: 2026 },
    "ligue-1": { league: 61, season: 2025 },
    "champions-league": { league: 2, season: 2025 },
    "premier-league": { league: 39, season: 2025 },
    "la-liga": { league: 140, season: 2025 },
    "bundesliga": { league: 78, season: 2025 },
    "serie-a": { league: 135, season: 2025 },
    "europa-league": { league: 3, season: 2025 },
    "primeira-liga": { league: 94, season: 2025 },
    "pro-league": { league: 144, season: 2025 },
    "eredivisie": { league: 88, season: 2025 },
};

const COMPETITION_BADGES = {
    "ligue-1": "https://r2.thesportsdb.com/images/media/league/badge/9f7z9d1742983155.png",
    "premier-league": "https://r2.thesportsdb.com/images/media/league/badge/gasy9d1737743125.png",
    "la-liga": "https://r2.thesportsdb.com/images/media/league/badge/ja4it51687628717.png",
    "serie-a": "https://r2.thesportsdb.com/images/media/league/badge/67q3q21679951383.png",
    "bundesliga": "https://r2.thesportsdb.com/images/media/league/badge/teqh1b1679952008.png",
    "champions-league": "https://r2.thesportsdb.com/images/media/league/badge/facv1u1742998896.png",
    "europa-league": "https://r2.thesportsdb.com/images/media/league/badge/mlsr7d1718774547.png",
    "eredivisie": "https://r2.thesportsdb.com/images/media/league/badge/5cdsu21725984946.png",
    "primeira-liga": "https://r2.thesportsdb.com/images/media/league/badge/lkfko71751917970.png",
    "coupe-du-monde": "https://r2.thesportsdb.com/images/media/league/badgearchive/z7ems41765247635.png",
    "pro-league": "https://r2.thesportsdb.com/images/media/league/badge/mjit7n1593634474.png",
    "coupe-de-france": "https://r2.thesportsdb.com/images/media/league/badge/l6fitb1546469041.png"
};

const Competition = () => {
    const { slug } = useParams();
    const c = getCompetitionBySlug(slug);
    const [news, setNews] = useState([]);
    const [newsLoading, setNewsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("news");

    const leagueName = MAP_SLUG_TO_LEAGUE_NAME[slug];
    const localLeagueData = leaguesData?.leagues?.[leagueName];
    const [viewMode, setViewMode] = useState("live");

    const [liveStandings, setLiveStandings] = useState(null);
    const [liveStandingsLoading, setLiveStandingsLoading] = useState(false);
    const [liveStandingsError, setLiveStandingsError] = useState(null);

    const [liveMatches, setLiveMatches] = useState(null);
    const [liveMatchesLoading, setLiveMatchesLoading] = useState(false);
    const [liveMatchesError, setLiveMatchesError] = useState(null);

    const [selectedGroupKey, setSelectedGroupKey] = useState("");

    useEffect(() => {
        setActiveTab("news");
        setViewMode("live");
    }, [slug, localLeagueData]);

    useEffect(() => {
        let cancelled = false;
        setNewsLoading(true);
        axios
            .get(`${API}/articles`)
            .then(({ data }) => {
                if (!cancelled) {
                    setNews(data);
                    setNewsLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setNewsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    useEffect(() => {
        if (!SUPPORTED_LIVE_SLUGS.includes(slug)) {
            setLiveStandings(null);
            setLiveMatches(null);
            return;
        }

        let cancelled = false;

        // Fetch standings
        setLiveStandingsLoading(true);
        setLiveStandingsError(null);
        axios
            .get(`${API}/competitions/${slug}/standings`)
            .then(({ data }) => {
                if (!cancelled) {
                    setLiveStandings(data);
                    setLiveStandingsLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Error fetching live standings:", err);
                    setLiveStandingsError("Impossible de charger le classement en direct.");
                    setLiveStandingsLoading(false);
                }
            });

        // Fetch matches
        setLiveMatchesLoading(true);
        setLiveMatchesError(null);
        axios
            .get(`${API}/competitions/${slug}/matches`)
            .then(({ data }) => {
                if (!cancelled) {
                    setLiveMatches(data);
                    setLiveMatchesLoading(false);
                    
                    // Set default selected matchday/group key
                    if (data && data.matches && data.matches.length > 0) {
                        const currentMatchday = data.season?.currentMatchday || 1;
                        
                        // Check if currentMatchday exists in matches
                        const matchdays = new Set(data.matches.filter(m => m.stage === "REGULAR_SEASON" || m.stage === "GROUP_STAGE").map(m => m.matchday));
                        
                        if (matchdays.has(currentMatchday)) {
                            setSelectedGroupKey(`matchday_${currentMatchday}`);
                        } else if (matchdays.size > 0) {
                            const sortedMatchdays = Array.from(matchdays).sort((a, b) => a - b);
                            setSelectedGroupKey(`matchday_${sortedMatchdays[0]}`);
                        } else {
                            setSelectedGroupKey(data.matches[0].stage);
                        }
                    }
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Error fetching live matches:", err);
                    setLiveMatchesError("Impossible de charger le calendrier des matchs.");
                    setLiveMatchesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [slug, viewMode]);

    const filteredNews = useMemo(() => {
        if (!c || !news || news.length === 0) return [];
        const keywords = [];
        if (c.slug) keywords.push(c.slug.replace("-", " "));
        if (c.name) {
            keywords.push(c.name.toLowerCase());
            // Add common variations
            if (c.slug === "champions-league") {
                keywords.push("champions league");
                keywords.push("ligue des champions");
                keywords.push("ucl");
            } else if (c.slug === "europa-league") {
                keywords.push("europa league");
                keywords.push("ligue europa");
                keywords.push("uel");
            } else if (c.slug === "coupe-de-france") {
                keywords.push("coupe de france");
                keywords.push("cdf");
            } else if (c.slug === "coupe-du-monde") {
                keywords.push("coupe du monde");
                keywords.push("mondial");
                keywords.push("world cup");
            } else if (c.slug === "la-liga") {
                keywords.push("la liga");
                keywords.push("liga");
                keywords.push("espagn");
            } else if (c.slug === "premier-league") {
                keywords.push("premier league");
                keywords.push("d1 anglaise");
                keywords.push("angleterre");
            } else if (c.slug === "serie-a") {
                keywords.push("serie a");
                keywords.push("calcio");
                keywords.push("italie");
            } else if (c.slug === "bundesliga") {
                keywords.push("bundesliga");
                keywords.push("allemagne");
            } else if (c.slug === "ligue-1") {
                keywords.push("ligue 1");
                keywords.push("l1");
            } else if (c.slug === "primeira-liga") {
                keywords.push("primeira liga");
                keywords.push("portugal");
            } else if (c.slug === "pro-league") {
                keywords.push("pro league");
                keywords.push("belgique");
            } else if (c.slug === "eredivisie") {
                keywords.push("eredivisie");
                keywords.push("pays-bas");
                keywords.push("hollande");
            }
        }

        return news.filter((item) => {
            const title = (item.title || "").toLowerCase();
            const excerpt = (item.excerpt || "").toLowerCase();
            const content = (item.content || "").toLowerCase();
            return keywords.some(
                (kw) => title.includes(kw) || excerpt.includes(kw) || content.includes(kw)
            );
        });
    }, [news, c]);    if (!c) return <Navigate to="/comparatif" replace />;

    const widgetConfig = COMPETITION_WIDGET_CONFIG[slug];

    const getZoneInfo = (rank) => {
        if (slug === "premier-league" || slug === "la-liga" || slug === "serie-a" || slug === "bundesliga") {
            const relegationRank = slug === "bundesliga" ? 17 : 18;
            if (rank <= 4) return { borderClass: "border-l-4 border-l-[#00C853]", bgClass: "bg-[#00C853]/5", text: "Ligue des Champions", color: "#00C853" };
            if (rank === 5) return { borderClass: "border-l-4 border-l-blue-500", bgClass: "bg-blue-500/5", text: "Ligue Europa", color: "#3B82F6" };
            if (rank === 6) return { borderClass: "border-l-4 border-l-[#FFB300]", bgClass: "bg-[#FFB300]/5", text: "Ligue Conférence", color: "#FFB300" };
            if (rank >= relegationRank) return { borderClass: "border-l-4 border-l-[#E63946]", bgClass: "bg-[#E63946]/5", text: "Relégation", color: "#E63946" };
        }
        if (slug === "ligue-1") {
            if (rank <= 3) return { borderClass: "border-l-4 border-l-[#00C853]", bgClass: "bg-[#00C853]/5", text: "Ligue des Champions", color: "#00C853" };
            if (rank === 4) return { borderClass: "border-l-4 border-l-[#00C853]/60", bgClass: "bg-[#00C853]/3", text: "Qualif. Ligue des Champions", color: "#00C853" };
            if (rank === 5) return { borderClass: "border-l-4 border-l-blue-500", bgClass: "bg-blue-500/5", text: "Ligue Europa", color: "#3B82F6" };
            if (rank === 6) return { borderClass: "border-l-4 border-l-[#FFB300]", bgClass: "bg-[#FFB300]/5", text: "Ligue Conférence", color: "#FFB300" };
            if (rank === 16) return { borderClass: "border-l-4 border-l-orange-500", bgClass: "bg-orange-500/5", text: "Barrage de Relégation", color: "#F97316" };
            if (rank >= 17) return { borderClass: "border-l-4 border-l-[#E63946]", bgClass: "bg-[#E63946]/5", text: "Relégation", color: "#E63946" };
        }
        if (slug === "champions-league") {
            if (rank <= 8) return { borderClass: "border-l-4 border-l-[#00C853]", bgClass: "bg-[#00C853]/5", text: "Huitièmes (Direct)", color: "#00C853" };
            if (rank >= 9 && rank <= 24) return { borderClass: "border-l-4 border-l-blue-500", bgClass: "bg-blue-500/5", text: "Barrages", color: "#3B82F6" };
            if (rank >= 25) return { borderClass: "border-l-4 border-l-[#E63946]", bgClass: "bg-[#E63946]/5", text: "Éliminé", color: "#E63946" };
        }
        return { borderClass: "border-l-4 border-l-transparent", bgClass: "", text: "", color: "" };
    };

    const getTeamColor = (teamName) => {
        let hash = 0;
        for (let i = 0; i < teamName.length; i++) {
            hash = teamName.charCodeAt(i) + ((hash << 5) - hash);
        }
        const hue = Math.abs(hash % 360);
        return `hsl(${hue}, 65%, 40%)`;
    };

    const getLegendZones = () => {
        if (slug === "premier-league" || slug === "la-liga" || slug === "serie-a" || slug === "bundesliga") {
            return [
                { text: "Ligue des Champions (1-4)", color: "#00C853" },
                { text: "Ligue Europa (5)", color: "#3B82F6" },
                { text: "Ligue Conférence (6)", color: "#FFB300" },
                { text: "Relégation", color: "#E63946" }
            ];
        }
        if (slug === "ligue-1") {
            return [
                { text: "Ligue des Champions (1-3)", color: "#00C853" },
                { text: "Qualif. Ligue des Champions (4)", color: "#00C853" },
                { text: "Ligue Europa (5)", color: "#3B82F6" },
                { text: "Ligue Conférence (6)", color: "#FFB300" },
                { text: "Barrage de Relégation (16)", color: "#F97316" },
                { text: "Relégation (17-18)", color: "#E63946" }
            ];
        }
        if (slug === "champions-league") {
            return [
                { text: "Huitièmes - Direct (1-8)", color: "#00C853" },
                { text: "Barrages des Huitièmes (9-24)", color: "#3B82F6" },
                { text: "Éliminé (25-36)", color: "#E63946" }
            ];
        }
        return [];
    };

    return (
        <div data-testid={`competition-page-${slug}`}>
            <SEO
                title={`Actualités, Calendrier & Classement ${c.name} 2026 | Pronospot.com`}
                description={`Retrouvez toutes les actualités, le calendrier complet, les matchs en direct et le classement de la ${c.name} en 2026.`}
                jsonLd={breadcrumbSchema([
                    { name: "Accueil", url: "https://pronospot.com/" },
                    { name: "Compétitions", url: "https://pronospot.com/comparatif" },
                    {
                        name: c.name,
                        url: `https://pronospot.com/competitions/${c.slug}`,
                    },
                ])}
            />
            <section className="container-x pt-10 pb-6">
                <Breadcrumbs
                    items={[
                        { label: "Compétitions" },
                        { label: c.name },
                    ]}
                />
                <div className="flex flex-wrap items-center gap-3 mb-3">
                    {COMPETITION_BADGES[slug] ? (
                        <img 
                            src={COMPETITION_BADGES[slug]} 
                            alt={c.name} 
                            className="w-12 h-12 object-contain" 
                        />
                    ) : (
                        <Trophy className="text-[#FFB300]" size={32} />
                    )}
                    <span className="chip">{localLeagueData?.country || c.country} — {localLeagueData?.confederation || c.season}</span>
                    {localLeagueData?.founded && (
                        <span className="chip !bg-[#FFB300]/10 !text-[#FFB300] !border-[#FFB300]/25">Fondé en {localLeagueData.founded}</span>
                    )}
                    {localLeagueData?.team_count && (
                        <span className="chip !bg-blue-500/10 !text-blue-400 !border-blue-500/25">{localLeagueData.team_count} équipes</span>
                    )}
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter">
                    {slug === "ligue-1" ? c.name : `Paris ${c.name}`}
                </h1>
                <p className="text-gray-400 mt-4 max-w-3xl text-lg mb-6">
                    {localLeagueData?.description || c.description}
                </p>
                {slug === "coupe-du-monde" && (
                    <Countdown targetDate="2026-06-11T18:00:00" />
                )}
            </section>

            {/* Top Performers Section */}
            {localLeagueData?.top_performers && (
                <section className="container-x mb-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up">
                        <div className="card-elev p-4 bg-[#162236]/50 backdrop-blur-sm border border-[#2A4A6B]/50 flex items-center gap-4">
                            <div className="p-3 bg-[#00C853]/15 text-[#00C853] rounded-xl shrink-0">
                                <Zap size={24} />
                            </div>
                            <div className="min-w-0">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">Meilleure Attaque</span>
                                <h4 className="font-heading font-bold text-white text-sm mt-0.5 truncate">{localLeagueData.top_performers.best_attack.team}</h4>
                                <p className="text-xs text-[#00C853] font-bold mt-0.5">
                                    {localLeagueData.top_performers.best_attack.goals_for || localLeagueData.top_performers.best_attack.goals_for === 0
                                        ? `${localLeagueData.top_performers.best_attack.goals_for} buts`
                                        : "0 but"}
                                </p>
                            </div>
                        </div>
                        <div className="card-elev p-4 bg-[#162236]/50 backdrop-blur-sm border border-[#2A4A6B]/50 flex items-center gap-4">
                            <div className="p-3 bg-[#E63946]/15 text-[#E63946] rounded-xl shrink-0">
                                <Shield size={24} />
                            </div>
                            <div className="min-w-0">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">Meilleure Défense</span>
                                <h4 className="font-heading font-bold text-white text-sm mt-0.5 truncate">{localLeagueData.top_performers.best_defence.team}</h4>
                                <p className="text-xs text-[#E63946] font-bold mt-0.5">
                                    {localLeagueData.top_performers.best_defence.goals_against || localLeagueData.top_performers.best_defence.goals_against === 0
                                        ? `${localLeagueData.top_performers.best_defence.goals_against} buts`
                                        : "0 but"}
                                </p>
                            </div>
                        </div>
                        <div className="card-elev p-4 bg-[#162236]/50 backdrop-blur-sm border border-[#2A4A6B]/50 flex items-center gap-4">
                            <div className="p-3 bg-[#FFB300]/15 text-[#FFB300] rounded-xl shrink-0">
                                <Trophy size={24} />
                            </div>
                            <div className="min-w-0">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">Plus de Victoires</span>
                                <h4 className="font-heading font-bold text-white text-sm mt-0.5 truncate">{localLeagueData.top_performers.most_wins.team}</h4>
                                <p className="text-xs text-[#FFB300] font-bold mt-0.5">
                                    {localLeagueData.top_performers.most_wins.wins || localLeagueData.top_performers.most_wins.wins === 0
                                        ? `${localLeagueData.top_performers.most_wins.wins} victoires`
                                        : "0 victoires"}
                                </p>
                            </div>
                        </div>
                        <div className="card-elev p-4 bg-[#162236]/50 backdrop-blur-sm border border-[#2A4A6B]/50 flex items-center gap-4">
                            <div className="p-3 bg-blue-500/15 text-blue-400 rounded-xl shrink-0">
                                <TrendingUp size={24} />
                            </div>
                            <div className="min-w-0">
                                <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider block">Meilleur Différentiel</span>
                                <h4 className="font-heading font-bold text-white text-sm mt-0.5 truncate">{localLeagueData.top_performers.best_goal_difference.team}</h4>
                                <p className="text-xs text-blue-400 font-bold mt-0.5">
                                    {localLeagueData.top_performers.best_goal_difference.goal_difference > 0
                                        ? `+${localLeagueData.top_performers.best_goal_difference.goal_difference}`
                                        : localLeagueData.top_performers.best_goal_difference.goal_difference}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Navigation par onglets si supportée par API-Football ou données locales */}
            {(widgetConfig || localLeagueData) && (
                <div className="container-x mb-8 flex border-b border-[#2A4A6B]">
                    <button
                        onClick={() => setActiveTab("news")}
                        className={`py-3 px-6 font-heading font-bold text-sm transition-all relative ${
                            activeTab === "news"
                                ? "text-[#00C853] border-b-2 border-b-[#00C853]"
                                : "text-gray-400 hover:text-slate-900 dark:hover:text-white"
                        }`}
                        style={{ marginBottom: "-1px" }}
                    >
                        Actualités
                    </button>
                    <button
                        onClick={() => setActiveTab("standings")}
                        className={`py-3 px-6 font-heading font-bold text-sm transition-all relative ${
                            activeTab === "standings"
                                ? "text-[#00C853] border-b-2 border-b-[#00C853]"
                                : "text-gray-400 hover:text-slate-900 dark:hover:text-white"
                        }`}
                        style={{ marginBottom: "-1px" }}
                    >
                        Classement
                    </button>
                    {widgetConfig && (
                        <button
                            onClick={() => setActiveTab("fixtures")}
                            className={`py-3 px-6 font-heading font-bold text-sm transition-all relative ${
                                activeTab === "fixtures"
                                    ? "text-[#00C853] border-b-2 border-b-[#00C853]"
                                    : "text-gray-400 hover:text-slate-900 dark:hover:text-white"
                            }`}
                            style={{ marginBottom: "-1px" }}
                        >
                            Calendrier & Matchs
                        </button>
                    )}
                </div>
            )}

            {/* Contenu de l'onglet Actualités (ou affiché par défaut si non supporté) */}
            {((!widgetConfig && !localLeagueData) || activeTab === "news") && (
                <section className="container-x pb-20" data-testid="competition-news">
                    <div className="flex items-center gap-2 mb-6">
                        <Newspaper size={24} className="text-[#00C853]" />
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
                            Actualités & Pronostics — {c.name}
                        </h2>
                    </div>

                    {newsLoading ? (
                        <div className="flex items-center justify-center gap-2 py-12 text-gray-400 text-sm">
                            <RefreshCw size={16} className="animate-spin" />
                            Chargement des actualités...
                        </div>
                    ) : filteredNews.length === 0 ? (
                        <div className="card-elev p-8 text-center bg-[#162236]/40 border border-[#2A4A6B]/30 rounded-xl">
                            <p className="text-gray-400 text-sm italic">
                                Aucune actualité disponible pour la {c.name} pour le moment.
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredNews.map((item, index) => (
                                <CompetitionNewsCard key={(item.slug || item.link) + index} item={item} index={index} />
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* Contenu de l'onglet Classement */}
            {(widgetConfig || localLeagueData) && activeTab === "standings" && (
                <section className="container-x pb-20 animate-fade-in">
                    <div className="card-elev p-6 bg-[#162236]/80 backdrop-blur-md">
                        <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            {COMPETITION_BADGES[slug] ? (
                                <img 
                                    src={COMPETITION_BADGES[slug]} 
                                    alt={c.name} 
                                    className="w-8 h-8 object-contain" 
                                />
                            ) : (
                                <Trophy className="text-[#FFB300]" />
                            )}
                            Classement — {c.name}
                        </h2>

                        {SUPPORTED_LIVE_SLUGS.includes(slug) ? (
                            <LiveStandingsTable
                                standings={liveStandings}
                                loading={liveStandingsLoading}
                                error={liveStandingsError}
                                slug={slug}
                                c={c}
                                viewMode={viewMode}
                                setViewMode={setViewMode}
                                widgetConfig={widgetConfig}
                                localLeagueData={localLeagueData}
                                getZoneInfo={getZoneInfo}
                                getTeamColor={getTeamColor}
                                getLegendZones={getLegendZones}
                            />
                        ) : (
                            <div>
                                <ApiFootballWidget
                                    type="standings"
                                    league={widgetConfig?.league}
                                    season={widgetConfig?.season}
                                />
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Contenu de l'onglet Calendrier & Matchs */}
            {widgetConfig && activeTab === "fixtures" && (
                <section className="container-x pb-20 animate-fade-in">
                    <div className="card-elev p-6 bg-[#162236]/80 backdrop-blur-md">
                        <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <Calendar className="text-[#00C853]" />
                            Calendrier complet & Matchs en direct — {c.name}
                        </h2>
                        {SUPPORTED_LIVE_SLUGS.includes(slug) ? (
                            <LiveMatchesList
                                matchesData={liveMatches}
                                loading={liveMatchesLoading}
                                error={liveMatchesError}
                                selectedGroupKey={selectedGroupKey}
                                setSelectedGroupKey={setSelectedGroupKey}
                                c={c}
                                getTeamColor={getTeamColor}
                            />
                        ) : (
                            <ApiFootballWidget
                                type="league"
                                league={widgetConfig.league}
                                season={widgetConfig.season}
                            />
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

const Countdown = ({ targetDate }) => {
    const calculateTimeLeft = () => {
        const difference = +new Date(targetDate) - +new Date();
        let timeLeft = {};

        if (difference > 0) {
            timeLeft = {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
            };
        } else {
            timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
        return timeLeft;
    };

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetDate]);

    const formatNumber = (num) => String(num).padStart(2, "0");

    const isOver = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

    if (isOver) {
        return (
            <div className="card-elev border-l-4 border-l-[#00C853] p-4 bg-gradient-to-r from-[#0D1B2A] to-[#1E3A5F] mb-6 flex items-center justify-between animate-fade-in">
                <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00C853] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-[#00C853]"></span>
                    </span>
                    <span className="font-heading font-bold text-white uppercase tracking-wider text-sm sm:text-base">
                        La Coupe du Monde 2026 a commencé ! Place aux actualités.
                    </span>
                </div>
                <Link to="/" className="btn-green !py-2 !px-4 text-xs font-semibold">
                    Voir les actualités
                </Link>
            </div>
        );
    }

    return (
        <div className="card-elev p-6 mb-8 bg-gradient-to-r from-[#0A1623] via-[#152B44] to-[#0A1623] border border-[#2A4A6B] relative overflow-hidden group shadow-lg shadow-black/30 rounded-xl">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#00C853]/5 to-[#FFB300]/5 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00C853]/40 to-transparent" />
            
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
                <div className="text-center md:text-left">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00C853]/15 border border-[#00C853]/30 mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] animate-pulse" />
                        <span className="text-[10px] sm:text-xs font-bold text-[#00C853] uppercase tracking-wider">
                            Événement Planétaire 🏆
                        </span>
                    </div>
                    <h3 className="font-heading font-bold text-white text-lg sm:text-xl">
                        Coup d'envoi de la Coupe du Monde 2026
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-400 mt-1">
                        Le premier match débute le 11 juin 2026. Préparez vos pronostics !
                    </p>
                </div>

                <div className="flex items-center gap-3 sm:gap-4 select-none">
                    <CountdownItem value={formatNumber(timeLeft.days)} label="Jours" />
                    <CountdownDivider />
                    <CountdownItem value={formatNumber(timeLeft.hours)} label="Heures" />
                    <CountdownDivider />
                    <CountdownItem value={formatNumber(timeLeft.minutes)} label="Min" />
                    <CountdownDivider />
                    <CountdownItem value={formatNumber(timeLeft.seconds)} label="Sec" />
                </div>
            </div>
        </div>
    );
};

const CountdownItem = ({ value, label }) => (
    <div className="flex flex-col items-center">
        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#0D1B2A]/90 border border-[#2A4A6B] flex items-center justify-center shadow-inner relative group-hover:border-[#00C853]/40 transition-colors">
            <span className="font-heading font-extrabold text-white text-xl sm:text-2xl tracking-tight tabular-nums">
                {value}
            </span>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-white/[0.02] to-transparent pointer-events-none" />
        </div>
        <span className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wider mt-1.5">
            {label}
        </span>
    </div>
);

const CountdownDivider = () => (
    <div className="text-xl sm:text-2xl font-extrabold text-[#2A4A6B] self-start mt-3 sm:mt-4">:</div>
);

const FALLBACK_IMAGES = [
    "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1540747737956-378724044402?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1518063319789-7217e6706b04?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1577223625816-7546f13df25d?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1504305754058-2f08ccd89a0a?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1431324155629-1a6edd1d141d?q=80&w=600&auto=format&fit=crop",
];

const CompetitionNewsCard = ({ item, index }) => {
    return (
        <Link
            to={`/articles/${item.slug}`}
            className="card-elev p-0 overflow-hidden flex flex-col group border border-[#2A4A6B]/30 hover:border-[#00C853]/40 animate-fade-in-up"
            style={{ animationDelay: `${Math.min(index * 60, 360)}ms` }}
        >
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-black/20">
                <img
                    src={item.image || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <span
                    className="absolute top-3 left-3 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider text-white border bg-[#00C853] border-[#00C853]"
                >
                    {item.category || "Pronostics"}
                </span>
            </div>

            <div className="p-5 flex-1 flex flex-col justify-between">
                <div>
                    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-2 font-bold uppercase tracking-wider">
                        <span>{item.author || "Pronospot AI"}</span>
                        <span>{item.date}</span>
                    </div>
                    <h3 className="font-heading font-bold text-white text-base leading-snug mb-2 group-hover:text-[#00C853] transition-colors line-clamp-3">
                        {item.title}
                    </h3>
                    {(item.excerpt || item.summary) && (
                        <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
                            {item.excerpt || item.summary}
                        </p>
                    )}
                </div>

                <div className="mt-4 pt-3 border-t border-[#2A4A6B]/50 text-[#00C853] text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Lire l'analyse
                    <ChevronRight size={12} />
                </div>
            </div>
        </Link>
    );
};

const SUPPORTED_LIVE_SLUGS = [
    "coupe-du-monde",
    "champions-league",
    "premier-league",
    "la-liga",
    "serie-a",
    "bundesliga",
    "ligue-1",
    "eredivisie",
    "primeira-liga"
];

const formatMatchDateTime = (utcDateStr) => {
    if (!utcDateStr) return "";
    try {
        const date = new Date(utcDateStr);
        const formatter = new Intl.DateTimeFormat('fr-FR', {
            timeZone: 'Europe/Paris',
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        
        const parts = formatter.formatToParts(date);
        let weekday = "";
        let day = "";
        let month = "";
        let hour = "";
        let minute = "";
        
        for (const part of parts) {
            if (part.type === 'weekday') weekday = part.value;
            else if (part.type === 'day') day = part.value;
            else if (part.type === 'month') month = part.value;
            else if (part.type === 'hour') hour = part.value;
            else if (part.type === 'minute') minute = part.value;
        }
        
        if (weekday) {
            weekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
        }
        
        return `${weekday} ${day}/${month} · ${hour}h${minute}`;
    } catch {
        return utcDateStr;
    }
};

const LiveStandingsTable = ({
    standings,
    loading,
    error,
    slug,
    c,
    viewMode,
    setViewMode,
    widgetConfig,
    localLeagueData,
    getZoneInfo,
    getTeamColor,
    getLegendZones
}) => {
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 text-sm gap-3">
                <RefreshCw size={24} className="animate-spin text-[#00C853]" id="standings-loader" />
                <span>Chargement du classement en direct...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card-elev p-6 text-center border-l-4 border-l-[#E63946]">
                <p className="text-[#E63946] text-sm font-semibold">{error}</p>
            </div>
        );
    }

    if (!standings || !standings.standings || standings.standings.length === 0) {
        return (
            <p className="text-gray-400 text-sm italic py-8 text-center">
                Aucun classement en direct disponible pour le moment.
            </p>
        );
    }

    return (
        <div className="space-y-6" data-testid="live-standings-table">

            {standings.standings.map((groupStandings, gIdx) => {
                const table = groupStandings.table || [];
                if (table.length === 0) return null;

                return (
                    <div key={gIdx} className="space-y-4">
                        {standings.standings.length > 1 && (
                            <h3 className="font-heading font-bold text-lg text-white mt-6">
                                {groupStandings.group || groupStandings.stage || "Classement"}
                            </h3>
                        )}

                        <div className="overflow-x-auto rounded-xl border border-[#2A4A6B] bg-[#0D1B2A]/40">
                            <table className="w-full text-sm text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-[#2A4A6B] bg-[#0D1B2A]/80 text-gray-400 font-semibold text-xs uppercase tracking-wider">
                                        <th className="py-3.5 px-4 text-center w-12">#</th>
                                        <th className="py-3.5 px-4 text-left">Équipe</th>
                                        <th className="py-3.5 px-3 text-center w-12">MJ</th>
                                        <th className="py-3.5 px-3 text-center w-10">V</th>
                                        <th className="py-3.5 px-3 text-center w-10">N</th>
                                        <th className="py-3.5 px-3 text-center w-10">D</th>
                                        <th className="py-3.5 px-3 text-center w-16 hidden md:table-cell">BP</th>
                                        <th className="py-3.5 px-3 text-center w-16 hidden md:table-cell">BC</th>
                                        <th className="py-3.5 px-3 text-center w-14">Diff</th>
                                        <th className="py-3.5 px-4 text-center w-16 font-bold text-white">Pts</th>
                                        <th className="py-3.5 px-4 text-center w-24 hidden sm:table-cell">Forme</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#2A4A6B]/50">
                                    {table.map((t) => {
                                        const zone = getZoneInfo(t.position);
                                        return (
                                            <tr
                                                key={t.position + t.team.name}
                                                className={`hover:bg-[#1E3A5F]/20 transition-all ${zone.bgClass} group`}
                                            >
                                                <td className={`py-3 px-4 text-center font-bold text-gray-400 ${zone.borderClass}`}>
                                                    {t.position}
                                                </td>
                                                <td className="py-3 px-4 font-bold text-white flex items-center">
                                                    {t.team.crest ? (
                                                        <img
                                                            src={t.team.crest}
                                                            alt={t.team.name}
                                                            className="w-6 h-6 object-contain mr-2.5 shrink-0"
                                                            onError={(e) => {
                                                                e.target.style.display = "none";
                                                            }}
                                                        />
                                                    ) : (
                                                        <span
                                                            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-extrabold text-white mr-2.5 shadow-md shrink-0 border border-white/10"
                                                            style={{ backgroundColor: getTeamColor(t.team.shortName || t.team.name) }}
                                                        >
                                                            {t.team.tla || (t.team.shortName || t.team.name).substring(0, 3).toUpperCase()}
                                                        </span>
                                                    )}
                                                    <span className="truncate group-hover:text-[#00C853] transition-colors">
                                                        {t.team.shortName || t.team.name}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-3 text-center text-gray-300 font-medium">{t.playedGames}</td>
                                                <td className="py-3 px-3 text-center text-gray-400">{t.won}</td>
                                                <td className="py-3 px-3 text-center text-gray-400">{t.draw}</td>
                                                <td className="py-3 px-3 text-center text-gray-400">{t.lost}</td>
                                                <td className="py-3 px-3 text-center text-gray-500 hidden md:table-cell">{t.goalsFor}</td>
                                                <td className="py-3 px-3 text-center text-gray-500 hidden md:table-cell">{t.goalsAgainst}</td>
                                                <td className={`py-3 px-3 text-center font-semibold ${t.goalDifference > 0 ? 'text-[#00C853]' : t.goalDifference < 0 ? 'text-[#E63946]' : 'text-gray-400'}`}>
                                                    {t.goalDifference > 0 ? `+${t.goalDifference}` : t.goalDifference}
                                                </td>
                                                <td className="py-3 px-4 text-center font-extrabold text-[#00C853] bg-[#00C853]/5 group-hover:bg-[#00C853]/10 transition-colors">
                                                    {t.points}
                                                </td>
                                                <td className="py-3 px-4 text-center hidden sm:table-cell">
                                                    <div className="flex items-center justify-center gap-1">
                                                        {t.form && t.form.split(',').map((f, fIdx) => {
                                                            const colorClass = f === 'W' ? 'bg-[#00C853]' : f === 'D' ? 'bg-gray-500' : 'bg-[#E63946]';
                                                            const label = f === 'W' ? 'V' : f === 'D' ? 'N' : 'D';
                                                            return (
                                                                <span
                                                                    key={fIdx}
                                                                    title={f === 'W' ? 'Victoire' : f === 'D' ? 'Nul' : 'Défaite'}
                                                                    className={`w-4 h-4 rounded text-[9px] font-bold text-white flex items-center justify-center shrink-0 ${colorClass}`}
                                                                >
                                                                    {label}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}

            {getLegendZones().length > 0 && (
                <div className="mt-4 p-4 rounded-xl border border-[#2A4A6B]/50 bg-[#0D1B2A]/20 flex flex-wrap gap-x-6 gap-y-2.5 items-center">
                    <div className="flex items-center gap-1.5 text-xs text-gray-400 font-semibold uppercase tracking-wider mr-2">
                        <Info size={14} className="text-[#00C853]" />
                        <span>Légende :</span>
                    </div>
                    {getLegendZones().map((z, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-300">
                            <span className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: z.color }} />
                            <span>{z.text}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const LiveMatchesList = ({
    matchesData,
    loading,
    error,
    selectedGroupKey,
    setSelectedGroupKey,
    c,
    getTeamColor
}) => {
    const matchGroups = useMemo(() => {
        if (!matchesData || !matchesData.matches) return [];
        
        const groups = [];
        const seen = new Set();
        
        matchesData.matches.forEach(m => {
            let key, label;
            if (m.stage === "REGULAR_SEASON" || m.stage === "GROUP_STAGE") {
                if (m.matchday) {
                    key = `matchday_${m.matchday}`;
                    label = `Journée ${m.matchday}`;
                } else {
                    key = m.stage;
                    label = m.stage === "GROUP_STAGE" ? "Phase de groupes" : m.stage;
                }
            } else {
                key = m.stage;
                const STAGE_LABELS = {
                    "PRELIMINARY_ROUND": "Tour préliminaire",
                    "QUALIFICATION": "Qualifications",
                    "QUALIFICATION_ROUND_1": "1er Tour de qualif.",
                    "QUALIFICATION_ROUND_2": "2e Tour de qualif.",
                    "QUALIFICATION_ROUND_3": "3e Tour de qualif.",
                    "PLAYOFFS": "Barrages",
                    "LAST_16": "8es de finale",
                    "QUARTER_FINALS": "Quarts de finale",
                    "SEMI_FINALS": "Demi-finales",
                    "FINAL": "Finale",
                    "THIRD_PLACE": "Match pour la 3e place"
                };
                label = STAGE_LABELS[m.stage] || m.stage;
            }
            
            if (key && !seen.has(key)) {
                seen.add(key);
                groups.push({ key, label, stage: m.stage, matchday: m.matchday });
            }
        });
        
        groups.sort((a, b) => {
            const order = ["REGULAR_SEASON", "GROUP_STAGE", "PLAYOFFS", "LAST_16", "QUARTER_FINALS", "SEMI_FINALS", "FINAL", "THIRD_PLACE"];
            const idxA = order.indexOf(a.stage);
            const idxB = order.indexOf(b.stage);
            
            if (idxA !== -1 && idxB !== -1) {
                if (idxA !== idxB) return idxA - idxB;
                if (a.matchday && b.matchday) return a.matchday - b.matchday;
            }
            if (idxA !== -1) return -1;
            if (idxB !== -1) return 1;
            return 0;
        });
        
        return groups;
    }, [matchesData]);

    const activeGroup = useMemo(() => {
        return matchGroups.find(g => g.key === selectedGroupKey) || matchGroups[0] || null;
    }, [matchGroups, selectedGroupKey]);

    const filteredMatches = useMemo(() => {
        if (!matchesData || !matchesData.matches || !activeGroup) return [];
        return matchesData.matches.filter(m => {
            if (activeGroup.matchday) {
                return (m.stage === "REGULAR_SEASON" || m.stage === "GROUP_STAGE") && m.matchday === activeGroup.matchday;
            }
            return m.stage === activeGroup.stage;
        });
    }, [matchesData, activeGroup]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400 text-sm gap-3">
                <RefreshCw size={24} className="animate-spin text-[#00C853]" id="matches-loader" />
                <span>Chargement du calendrier en direct...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card-elev p-6 text-center border-l-4 border-l-[#E63946]">
                <p className="text-[#E63946] text-sm font-semibold">{error}</p>
            </div>
        );
    }

    if (matchGroups.length === 0) {
        return (
            <p className="text-gray-400 text-sm italic py-8 text-center">
                Aucun match planifié disponible pour le moment.
            </p>
        );
    }

    const getStatusLabel = (status) => {
        switch (status) {
            case "FINISHED": return "Terminé";
            case "IN_PLAY": return "En direct";
            case "PAUSED": return "Mi-temps";
            case "POSTPONED": return "Reporté";
            case "CANCELLED": return "Annulé";
            case "SUSPENDED": return "Suspendu";
            default: return "À venir";
        }
    };

    return (
        <div className="space-y-6" data-testid="live-matches-list">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-[#2A4A6B]/30">
                <h3 className="font-heading font-bold text-lg text-white">
                    {activeGroup ? activeGroup.label : "Matchs"}
                </h3>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 font-semibold">Choisir un tour :</span>
                    <select
                        value={selectedGroupKey}
                        onChange={(e) => setSelectedGroupKey(e.target.value)}
                        className="bg-[#0D1B2A] border border-[#2A4A6B] text-white rounded-lg px-3 py-1.5 text-xs font-semibold outline-none focus:border-[#00C853] transition-colors cursor-pointer"
                    >
                        {matchGroups.map((group) => (
                            <option key={group.key} value={group.key}>
                                {group.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredMatches.map((m) => {
                    const isCompleted = m.status === "FINISHED";
                    const isLive = m.status === "IN_PLAY" || m.status === "PAUSED";
                    
                    const scoreHome = m.score?.fullTime?.home;
                    const scoreAway = m.score?.fullTime?.away;

                    return (
                        <div 
                            key={m.id} 
                            className={`p-4 bg-[#162236]/65 border ${
                                isLive 
                                    ? "border-[#00C853] shadow-[0_0_15px_rgba(0,200,83,0.1)]" 
                                    : "border-[#2A4A6B]/50"
                            } rounded-xl text-xs space-y-3 hover:border-[#00C853]/40 transition-colors shadow-md relative overflow-hidden group`}
                        >
                            {isLive && (
                                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00C853] to-transparent animate-pulse" />
                            )}
                            
                            <div className="flex justify-between items-center text-[10px] text-gray-500 font-bold uppercase tracking-wider pb-2 border-b border-[#2A4A6B]/15">
                                <span className="font-mono text-gray-400">{formatMatchDateTime(m.utcDate)}</span>
                                <span className={`font-semibold text-[9px] px-2 py-0.5 rounded-full ${
                                    isCompleted 
                                        ? "bg-slate-800 text-gray-400" 
                                        : isLive 
                                            ? "bg-[#00C853]/15 text-[#00C853] animate-pulse" 
                                            : "bg-[#FFB300]/10 text-[#FFB300]"
                                }`}>
                                    {getStatusLabel(m.status)}
                                </span>
                            </div>

                            <div className="space-y-2.5">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2 truncate max-w-[80%]">
                                        {m.homeTeam.crest ? (
                                            <img 
                                                src={m.homeTeam.crest} 
                                                alt={m.homeTeam.name} 
                                                className="w-5 h-5 object-contain shrink-0" 
                                                onError={(e) => { e.target.style.display = "none"; }}
                                            />
                                        ) : (
                                            <span 
                                                className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black text-white shrink-0 border border-white/10" 
                                                style={{ backgroundColor: getTeamColor(m.homeTeam.shortName || m.homeTeam.name) }}
                                            >
                                                {m.homeTeam.tla || (m.homeTeam.shortName || m.homeTeam.name).substring(0, 2).toUpperCase()}
                                            </span>
                                        )}
                                        <span className={`font-semibold truncate text-sm ${
                                            isCompleted && scoreHome < scoreAway ? "text-gray-500 line-through" : "text-white"
                                        }`}>
                                            {m.homeTeam.shortName || m.homeTeam.name}
                                        </span>
                                    </div>
                                    <span className={`font-mono text-sm font-extrabold px-2 py-0.5 rounded ${
                                        isLive ? "bg-[#00C853]/10 text-[#00C853]" : "bg-[#0D1B2A] text-white"
                                    }`}>
                                        {scoreHome !== null && scoreHome !== undefined ? scoreHome : "-"}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-2 truncate max-w-[80%]">
                                        {m.awayTeam.crest ? (
                                            <img 
                                                src={m.awayTeam.crest} 
                                                alt={m.awayTeam.name} 
                                                className="w-5 h-5 object-contain shrink-0" 
                                                onError={(e) => { e.target.style.display = "none"; }}
                                            />
                                        ) : (
                                            <span 
                                                className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-black text-white shrink-0 border border-white/10" 
                                                style={{ backgroundColor: getTeamColor(m.awayTeam.shortName || m.awayTeam.name) }}
                                            >
                                                {m.awayTeam.tla || (m.awayTeam.shortName || m.awayTeam.name).substring(0, 2).toUpperCase()}
                                            </span>
                                        )}
                                        <span className={`font-semibold truncate text-sm ${
                                            isCompleted && scoreAway < scoreHome ? "text-gray-500 line-through" : "text-white"
                                        }`}>
                                            {m.awayTeam.shortName || m.awayTeam.name}
                                        </span>
                                    </div>
                                    <span className={`font-mono text-sm font-extrabold px-2 py-0.5 rounded ${
                                        isLive ? "bg-[#00C853]/10 text-[#00C853]" : "bg-[#0D1B2A] text-white"
                                    }`}>
                                        {scoreAway !== null && scoreAway !== undefined ? scoreAway : "-"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Competition;
